import type { TripAssignment } from "./assignment";
import type { TailLock } from "./boardOps";
import { postLegPay } from "./crewPay";
import { findLogForTrip, stampLogFromClosedTrip } from "./flightlog";
import { acceptedQuoteRevenueCad, isRevenueCharter, postCharterIfNeeded, refreshCharterReserves } from "./ledger";
import { logbookFromFlightLog } from "./logbook";
import { applyHoursToTail, flightEngineAdds, hoursFromTrip } from "./maintenance";
import type { CrewLedgerEntry } from "./opsLedger";
import { applyPostFlightTimes } from "./postflight";
import {
  closeTelemetrySession,
  findTripTelemetry,
  tripEngineHoursForReserve,
  tripFuelUsedGal,
  tripHoursForQuote,
  type TailTelemetrySession,
  type TailTripTelemetry,
} from "./simTelemetry";
import { ofpPlannedFuelGal } from "./tripFuel";
import type { QuoteOpts } from "./ledger";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyAccount,
  CrewMember,
  FlightLogEntry,
  Mission,
  OpsDeskSettings,
  PilotLogbookEntry,
  RateTables,
  SceneryAirport,
  Squawk,
} from "./types";

export type SettleSlices = {
  missions: Mission[];
  flightLogs: FlightLogEntry[];
  fleet: AircraftSpec[];
  squawks: Squawk[];
  account: CompanyAccount;
  rates: RateTables;
  catalogs?: QuoteOpts["catalogs"];
  tripTelemetry?: TailTripTelemetry[];
  /** Open session. Completion can land before the leg has closed into `tripTelemetry`. */
  liveSession?: TailTelemetrySession | null;
  /** Variable DOC. Set Mx / engine override company reserves for this tail. */
  opsDesk?: OpsDeskSettings | null;
  assignments?: TripAssignment[];
  crew?: CrewMember[];
  crewLedger?: CrewLedgerEntry[];
  scenery?: SceneryAirport[];
  logbook?: PilotLogbookEntry[];
  currency?: "CAD" | "USD" | "EUR" | "GBP";
  tailLocks?: TailLock[];
  vaId?: string;
  /** Inbox desk quotes. Revenue posts the accepted figure for this mission. */
  inbox?: CharterRequest[];
};

export type SettleResult = SettleSlices & {
  posted: boolean;
  hoursApplied: boolean;
  notice?: string;
};

/**
 * On charter / ferry complete: add block (or planned) hours, post ledger once,
 * park ferry tails in_mx. Idempotent via mission.hoursApplied / ledgerPosted.
 */
export function settleCompletedTrip(slices: SettleSlices, tripId: string, now = new Date()): SettleResult {
  const mission = slices.missions.find((m) => m.id === tripId);
  if (!mission) {
    return { ...slices, posted: false, hoursApplied: false };
  }
  const alreadyDone = mission.status === "completed" || mission.status === "cancelled";
  if (mission.status === "cancelled") {
    return { ...slices, posted: false, hoursApplied: false };
  }

  const rawLog = findLogForTrip(slices.flightLogs, tripId);
  const telEarly = findTripTelemetry(slices.tripTelemetry ?? [], {
    tripId,
    flightLogId: rawLog?.id,
    registration: mission.registration,
  });
  const ownsTrip = Boolean(
    telEarly && (telEarly.tripId === tripId || (rawLog && telEarly.flightLogId === rawLog.id)),
  );
  const session = slices.liveSession;
  const sessionOwns = Boolean(
    session &&
      (session.tripId === tripId || (rawLog && session.flightLogId === rawLog.id)) &&
      (session.offBlocksUtc || session.airborneUtc || session.landedUtc),
  );
  const liveSnap = sessionOwns && session ? closeTelemetrySession({ ...session, closed: true }) : null;
  const telOwned = (ownsTrip ? telEarly : undefined) ?? liveSnap ?? undefined;
  const stampedLog = rawLog && telOwned ? stampLogFromClosedTrip(rawLog, telOwned, slices.scenery ?? []) : rawLog;
  let flightLogs = stampedLog
    ? slices.flightLogs.map((row) => (row.id === stampedLog.id ? stampedLog : row))
    : slices.flightLogs;
  const log = stampedLog;
  let missions = slices.missions.map((m) =>
    m.id === tripId
      ? {
          ...m,
          status: "completed" as const,
          lifecycle: "completed" as const,
          completedAt: m.completedAt ?? now.toISOString(),
          acceptedAt: m.acceptedAt ?? now.toISOString(),
        }
      : m,
  );
  const settled = missions.find((m) => m.id === tripId)!;

  let fleet = slices.fleet;
  let hoursApplied = Boolean(settled.hoursApplied);
  const tel =
    telOwned ??
    findTripTelemetry(slices.tripTelemetry ?? [], {
      tripId,
      flightLogId: log?.id,
      registration: settled.registration,
    });
  if (!settled.hoursApplied) {
    const hours = hoursFromTrip(settled, log, {
      userOverrideHours: log?.arrival?.engineHours,
      simEngineHours: tripHoursForQuote(tel),
      ofpPlannedHours: Number(settled.ofpAttachment?.eteSec) > 0 ? Number(settled.ofpAttachment?.eteSec) / 3600 : undefined,
    });
    const trackedTenths = Boolean(
      tel && typeof tel.engineMxHours === "number" && Number.isFinite(tel.engineMxHours) && tel.engineMxHours > 0,
    );
    const enginePosted = trackedTenths ? tel!.engineMxHours! : (tel?.engineHours ?? 0);
    const tail = fleet.find((a) => a.id === settled.aircraftId || a.registration === settled.registration);
    const engineEach = trackedTenths ? flightEngineAdds(tail, tel?.engineHoursEach, enginePosted) : undefined;
    const fromSim = Boolean(tel && (enginePosted > 0 || tel.engineHours > 0 || tel.airTimeHours > 0 || (tel.apuHours ?? 0) > 0));
    const postedHours = fromSim
      ? {
          airframe: tel?.airTimeHours ?? 0,
          engine: enginePosted,
          apu: tel?.apuHours ?? 0,
          cycle: (tel?.airTimeHours ?? 0) > 0,
          engineEach,
        }
      : { airframe: hours, engine: hours, apu: 0, cycle: true };
    fleet = fleet.map((a) => {
      if (a.id !== settled.aircraftId && a.registration !== settled.registration) return a;
      let next = fromSim
        ? applyPostFlightTimes(
            a,
            {
              airTimeHours: tel?.airTimeHours ?? 0,
              engineHours: enginePosted,
              engineHoursEach: engineEach,
              apuHours: tel?.apuHours ?? 0,
              destIcao: settled.destIcao,
            },
            slices.squawks,
          )
        : applyHoursToTail(a, hours, settled.destIcao, slices.squawks);
      if (settled.isMaintenance) {
        next = { ...next, status: "in_mx", available: false };
      }
      return next;
    });
    hoursApplied = true;
    missions = missions.map((m) =>
      m.id === tripId ? { ...m, hoursApplied: true, postedHours, crashBasis: undefined } : m,
    );
  }

  let account = slices.account;
  let posted = Boolean(settled.ledgerPosted);
  const current = missions.find((m) => m.id === tripId)!;
  if (!current.ledgerPosted) {
    const aircraft = slices.fleet.find((a) => a.id === current.aircraftId || a.registration === current.registration);
    const result = postCharterIfNeeded(account, current, slices.rates, log, now, {
      catalogs: slices.catalogs,
      aircraft,
      homeCurrency: account.homeCurrency ?? slices.catalogs?.currency,
      actualFuelGal: log?.arrival?.fuelUsedGal,
      simFuelGal: tripFuelUsedGal(tel) ?? undefined,
      ofpPlannedFuelGal: ofpPlannedFuelGal(current.ofpAttachment) ?? undefined,
      simHours: tripEngineHoursForReserve(tel) ?? undefined,
      opsDesk: slices.opsDesk,
      acceptedRevenueCad: acceptedQuoteRevenueCad(current, slices.inbox) ?? undefined,
    });
    account = result.account;
    posted = result.posted;
    missions = missions.map((m) => (m.id === tripId ? { ...m, ledgerPosted: true } : m));
  }

  const bits: string[] = [];
  if (hoursApplied && !mission.hoursApplied) {
    bits.push(`HOURS  ${settled.registration}`);
  }
  if (posted && isRevenueCharter(settled) && !mission.ledgerPosted) {
    bits.push(`LEDGER  ${settled.callsign}`);
  }
  if (settled.isMaintenance && !mission.hoursApplied) {
    bits.push(`IN MX  ${settled.registration}`);
  }

  let crewLedger = slices.crewLedger ?? [];
  let logbook = slices.logbook ?? [];
  const settledMission = missions.find((row) => row.id === tripId)!;
  if (log) {
    crewLedger = postLegPay(
      {
        crewLedger,
        assignments: slices.assignments,
        crew: slices.crew,
        opsDesk: slices.opsDesk,
        currency: slices.currency ?? slices.account.homeCurrency,
        tailLocks: slices.tailLocks,
        vaId: slices.vaId,
      },
      settledMission,
      log,
      now,
    );
    const aircraft = slices.fleet.find(
      (row) => row.id === settledMission.aircraftId || row.registration === settledMission.registration,
    );
    const entry = logbookFromFlightLog(log, {
      crewId: slices.assignments?.find((row) => row.missionId === tripId && !row.superseded)?.crewId,
      crewName: log.assignedPilot,
      aircraftType: aircraft?.icaoType,
      role: "PIC",
      now,
    });
    if (entry.originIcao && entry.destIcao && entry.flightTimeMin > 0) {
      logbook = [entry, ...logbook.filter((row) => row.flightLogId !== log.id)];
    }
  }

  return {
    missions,
    flightLogs,
    fleet,
    squawks: slices.squawks,
    account,
    rates: slices.rates,
    crewLedger,
    logbook,
    assignments: slices.assignments,
    crew: slices.crew,
    scenery: slices.scenery,
    opsDesk: slices.opsDesk,
    posted,
    hoursApplied,
    notice: bits.length ? bits.join("  ·  ") : alreadyDone && mission.ledgerPosted ? undefined : bits.join("  ·  ") || undefined,
  };
}

/**
 * A leg can complete while its session is still open. When that session later
 * closes, write its clocks onto the log and replace crew pay / the logbook row.
 */
export function stampDeskFromClosedTrips(slices: SettleSlices): {
  flightLogs: FlightLogEntry[];
  crewLedger: CrewLedgerEntry[];
  logbook: PilotLogbookEntry[];
  account: CompanyAccount;
} {
  let flightLogs = slices.flightLogs;
  let crewLedger = slices.crewLedger ?? [];
  let logbook = slices.logbook ?? [];
  let account = slices.account;
  for (const trip of slices.tripTelemetry ?? []) {
    const log =
      (trip.flightLogId ? flightLogs.find((row) => row.id === trip.flightLogId) : undefined) ??
      (trip.tripId ? flightLogs.find((row) => row.tripId === trip.tripId) : undefined);
    if (!log) continue;
    if (trip.tripId && log.tripId !== trip.tripId && trip.flightLogId !== log.id) continue;
    const stamped = stampLogFromClosedTrip(log, trip, slices.scenery ?? []);
    flightLogs = flightLogs.map((row) => (row.id === log.id ? stamped : row));
    const mission = slices.missions.find((row) => row.id === stamped.tripId);
    if (!mission || mission.status !== "completed") continue;
    crewLedger = postLegPay(
      {
        crewLedger,
        assignments: slices.assignments,
        crew: slices.crew,
        opsDesk: slices.opsDesk,
        currency: slices.currency ?? slices.account.homeCurrency,
        tailLocks: slices.tailLocks,
        vaId: slices.vaId,
      },
      mission,
      stamped,
    );
    const aircraft = slices.fleet.find(
      (row) => row.id === mission.aircraftId || row.registration === mission.registration,
    );
    const entry = logbookFromFlightLog(stamped, {
      crewId: slices.assignments?.find((row) => row.missionId === mission.id && !row.superseded)?.crewId,
      crewName: stamped.assignedPilot,
      aircraftType: aircraft?.icaoType,
      role: "PIC",
    });
    if (entry.originIcao && entry.destIcao && entry.flightTimeMin > 0) {
      logbook = [entry, ...logbook.filter((row) => row.flightLogId !== stamped.id)];
    }
    account = refreshCharterReserves(account, mission, slices.rates, stamped, new Date(), {
      catalogs: slices.catalogs,
      aircraft,
      homeCurrency: account.homeCurrency ?? slices.catalogs?.currency,
      actualFuelGal: stamped.arrival?.fuelUsedGal,
      simFuelGal: tripFuelUsedGal(trip) ?? undefined,
      ofpPlannedFuelGal: ofpPlannedFuelGal(mission.ofpAttachment) ?? undefined,
      simHours: tripEngineHoursForReserve(trip) ?? undefined,
      opsDesk: slices.opsDesk,
      acceptedRevenueCad: acceptedQuoteRevenueCad(mission, slices.inbox) ?? undefined,
    });
  }
  return { flightLogs, crewLedger, logbook, account };
}
